import { uiText } from '../../utils/i18n';
import { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useT } from '../../utils/i18n';
import { getSkillDisplayName, getSkillIcon } from '../../data/skill-meta';
import styles from './SkillsPanel.module.css';

interface SkillInfo {
  name: string;
  description: string;
  userInvocable: boolean;
  context?: string;
  allowedTools?: string;
  content: string;
}

interface SkillsPanelProps {
  botName: string;
}

// Official skills shipped with NovaX or curated marketplace
const OFFICIAL_SKILLS = new Set([
  'metaschedule', 'skill-hub', 'metaskill', 'novax',
  'web-design-engineer', 'web-video-presentation', 'gpt-image-2', 'kb-retriever',
  'steve-jobs-perspective', 'munger-perspective', 'paul-graham-perspective',
  'elon-musk-perspective', 'feynman-perspective',
]);

function stripFrontmatter(content: string | undefined): string {
  if (!content) return '';
  return content.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, '');
}

export function SkillsPanel({ botName }: SkillsPanelProps) {
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SkillInfo | null>(null);
  const t = useT();

  const token = localStorage.getItem('novax:token') || '';

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/installed-skills/${encodeURIComponent(botName)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSkills(data.skills || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [botName, token]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // Detail view
  if (selected) {
    return (
      <div className={styles.container}>
        <div className={styles.detailHeader}>
          <button className={styles.backBtn} onClick={() => setSelected(null)}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('skill.back')}
          </button>
          <span className={styles.detailIcon}>{getSkillIcon(selected.name)}</span>
          <span className={styles.detailName}>{getSkillDisplayName(selected.name)}</span>
          {selected.userInvocable && <span className={styles.invocableTag}>{t('skill.invocable')}</span>}
        </div>
        <div className={styles.detailBody}>
          <div className={styles.detailMeta}>
            {selected.context && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('skill.execMode')}</span>
                <span className={styles.metaValue}>{selected.context}</span>
              </div>
            )}
            {selected.allowedTools && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('skill.allowedTools')}</span>
                <span className={styles.metaValue}>{selected.allowedTools}</span>
              </div>
            )}
          </div>
          <div className={styles.detailContent}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {stripFrontmatter(selected.content)}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('skill.installed')}</span>
        <span className={styles.headerCount}>{skills.length}</span>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>{t('chat.connecting')}</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : skills.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>⚡</div>
            <div className={styles.emptyTitle}>{t('skill.noSkills')}</div>
            <div className={styles.emptyHint}>{t('skill.noSkillsHint')}</div>
          </div>
        ) : (
          <div className={styles.grid}>
            {skills.map((skill) => (
              <button key={skill.name} className={styles.card} onClick={() => setSelected(skill)}>
                <div className={styles.cardTop}>
                  <span className={styles.cardIcon}>{getSkillIcon(skill.name)}</span>
                  {OFFICIAL_SKILLS.has(skill.name) ? (
                    <span className={styles.officialBadge}>{t('skill.official')}</span>
                  ) : (
                    <span className={styles.customBadge}>{t('skill.custom')}</span>
                  )}
                </div>
                <div className={styles.cardName}>{getSkillDisplayName(skill.name)}</div>
                <div className={styles.cardDesc}>
                  {skill.description.length > 80 ? skill.description.slice(0, 80) + '...' : skill.description || uiText("无描述")}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
